import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialogModule,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectModule, MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { take } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TaskListTemplate } from '@tasks/tasks.types';
import { RelatedToType } from 'app/shared/enums';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskActions } from '@tasks/state/actions';
import { Store } from '@ngxs/store';
import { DirectivesModule } from '@shared/directives/directives.module';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TaskListTemplateService } from '@tasks/services/task-list-template.service';
import { TsButtonModule } from 'tsui';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'create-task-list',
  templateUrl: './create-task-list.component.html',
  styleUrls: ['./create-task-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    DirectivesModule,
    MatLegacyDialogModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyInputModule,
    MatProgressSpinnerModule,
    TsButtonModule,
  ],
})
export class CreateTaskListComponent extends OnDestroyMixin implements OnInit {
  public createTaskListForm: UntypedFormGroup;
  public isLoading: boolean;
  public debounceLoading: boolean = false;
  public taskListTemplates: TaskListTemplate[];

  private tasksService = inject(TasksService);
  private tasksListTempalteService = inject(TaskListTemplateService);
  private store = inject(Store);
  private destroyRef = inject(DestroyRef);

  get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.createTaskListForm.pristine || this.createTaskListForm.invalid;
  }

  constructor(
    private readonly matDialogRef: MatDialogRef<CreateTaskListComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchTaskListTemplates(this.data?.applicableType);
    this.buildForm();
  }

  private buildForm(): void {
    this.createTaskListForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      task_list_template_id: [''],
    });
  }

  private fetchTaskListTemplates(type?: RelatedToType.Account | RelatedToType.Property | RelatedToType.Project): void {
    this.tasksListTempalteService
      .getTaskListTemplates(type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((taskListTemplates) => {
        this.taskListTemplates = taskListTemplates;
      });
  }

  public onSelectedTaskListTemplate(event: MatSelectChange): void {
    const index = this.taskListTemplates.findIndex((item) => item.id === event.value);
    let template = this.taskListTemplates[index];
    this.createTaskListForm.get('name').setValue(template.name);
  }

  public discard(): void {
    this.matDialogRef.close();
  }

  public save(): void {
    this.createTaskListForm.disable();
    this.isLoading = true;
    const vals = this.createTaskListForm.value;
    this.store.dispatch(new TaskActions.CreateTaskList(vals)).subscribe(() => {
      this.matDialogRef.close();
      this.isLoading = false;
    });
  }

  // Function attached to debounce-click directive to flag the button's disable state.
  public isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }
}
